import { useTranslation } from "react-i18next";

import { v7 as uuidv7 } from "uuid";
import { useVehiclesTableStore } from "@/stores/useTableStore";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { inputFieldMarginBottom } from "../../../../../../shared";
import Checkbox from "../../../../../CheckBox/Checkbox";
import InputField from "../../../../../InputField/InputField";

interface IAddVehicleDialogContent {
  payload: {
    imei: string | null;
    name: string | null;
    comment: string | null;
    disable: boolean;
  };
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImeiValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDescriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  toggleVehicleDeactivate: () => void;
  imeiErrorText?: string | null;
  validateImei: (event: any) => void;
  validateName: (event: any) => void;
  nameErrorText: string | null;
  validateDescription: (event: any) => void;
  descriptionErrorText: string | null;
  isLoading: boolean;
}

const AddVehicleDialogContent = ({
  payload,
  onNameChange,
  onImeiValueChange,
  onDescriptionChange,
  toggleVehicleDeactivate,
  imeiErrorText,
  validateImei,
  validateName,
  nameErrorText,
  validateDescription,
  descriptionErrorText,
  isLoading
}: IAddVehicleDialogContent) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const autoAssignVrnEnabled = useVehiclesTableStore(
    state => state.autoAssignVrnEnabled
  );

  const isVrnOptional = Boolean(autoAssignVrnEnabled);
  const optionalString = `(${t("Dialog##optional")})`;

  return (
    <>
      <InputField
        id={uuidv7()}
        size="medium"
        data-testid="add-vehicle-company-name"
        fullWidth
        readOnly
        labelLeft={t("Dialog##company")}
        placeholder={user?.selectedCompany.name}
        customStyle={inputFieldMarginBottom}
      />

      <InputField
        id={uuidv7()}
        testId="vehicle-number-input-field"
        size="medium"
        value={payload.name ?? ""}
        fullWidth
        infoTooltip={
          isVrnOptional
            ? t("Dialog##vehicle registration number tooltip")
            : undefined
        }
        required
        errorText={nameErrorText}
        onBlur={validateName}
        onChange={onNameChange}
        labelLeft={t("Dialog##vehicle number")}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <InputField
        id={uuidv7()}
        data-testid="imei-input-field"
        size="medium"
        value={payload.imei ?? ""}
        fullWidth
        errorText={imeiErrorText}
        onBlur={validateImei}
        onChange={onImeiValueChange}
        labelAddition={optionalString}
        labelLeft={t("Dialog##device imei")}
        placeholder={t("Dialog##imei placeholder")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <InputField
        id={uuidv7()}
        data-testid="description-input-field"
        size="medium"
        fullWidth
        value={payload.comment ?? ""}
        errorText={descriptionErrorText}
        onBlur={validateDescription}
        onChange={onDescriptionChange}
        labelLeft={t("Dialog##description required")}
        labelAddition={optionalString}
        placeholder={t("Dialog##enter here")}
        customStyle={inputFieldMarginBottom}
        disabled={isLoading}
      />

      <div>
        <Checkbox
          id={uuidv7()}
          info={t("Dialog##deactivate vehicle description")}
          label={t("Dialog##deactivate vehicle")}
          onChange={toggleVehicleDeactivate}
          disabled={isLoading}
        />
      </div>
    </>
  );
};

export default AddVehicleDialogContent;
